/*
 * File: addressesApi.ts
 * Project: rosita
 * -----
 * Last Modified: 24 Feb 2022 11:31:18 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import lodash from 'lodash';
import { ReduxState } from 'redux/store';
import { Address, AddressPayload, GetAddressesParams, GetAddressesResponse } from './models/addresses';

/* ******************** Base Query******************** */
const baseUrl = process.env.REACT_APP_CHUCKIE_SUE_BASE_URL || '';
const functionsKey = process.env.REACT_APP_API_HOST_KEY_CHUCKIE_SUE || '';

export const addressesBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  const businessId = (api.getState() as ReduxState).app.acuityContext?.selectedCustomer.business.id;
  const urlEnd = typeof args === 'string' ? args : args.url;
  const isDiagnostic = typeof args !== 'string' && (args.url === '/diagnostics/version' || args.url === '/diagnostics/apiName');

  const isRootLevel = typeof args !== 'string' && (args.url?.startsWith('/addresses') || args.url === 'addresses') && !businessId;

  // Only require businessId for non-diagnostic, non-root-level endpoints
  if (!isDiagnostic && !isRootLevel && !businessId) {
    return {
      error: {
        status: 400,
        statusText: 'Bad Request',
        data: 'No business ID received'
      }
    };
  }

  const adjustedUrl = isRootLevel ? urlEnd : `businesses/${businessId}/${urlEnd}`;
  const adjustedArgs = typeof args === 'string' ? adjustedUrl : { ...args, url: adjustedUrl };

  return fetchBaseQuery({
    baseUrl,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as ReduxState).app.accessToken;

      if (token) {
        headers.set('authorization', `Bearer ${token}`);
        headers.set('x-functions-key', functionsKey);
        headers.set('Content-Type', 'application/json');
      }

      return headers;
    }
  })(adjustedArgs, api, extraOptions);
};

export const chuckieSueAddressesApi = createApi({
  reducerPath: 'chuckieSueAddressesApi',
  baseQuery: addressesBaseQuery,
  tagTypes: ['Addresses', 'Address'],
  endpoints: (builder) => ({
    getAddresses: builder.query<GetAddressesResponse, GetAddressesParams>({
      query: (params) => ({
        url: 'addresses',
        params: lodash.pickBy(params, (v) => v !== undefined)
      }),
      // providesTags: ['Addresses'],
      onCacheEntryAdded: async (_arg, _api) => {
        try {
          await _api.cacheDataLoaded;
          const { originalArgs } = _api.getCacheEntry();
          const { queries } = _api.getState().chuckieSueAddressesApi;
          const lastQueryArgs = { ...originalArgs, offset: _arg.offset - 25 };
          const lastQueryData = Object.values(queries).find((value) => lodash.isEqual(value?.originalArgs, lastQueryArgs));

          if (lastQueryData) {
            _api.updateCachedData((draft) => {
              draft.data.unshift(...(lastQueryData.data as any).data);
            });
          }
        } catch (error) {
          //
        }
      }
    }),
    getAddress: builder.query<Address, string>({
      providesTags: ['Address'],
      query: (addressId) => ({
        url: `addresses/${addressId === 'none' ? null : addressId}`
      })
    }),
    createAddress: builder.mutation<Address, { payload: AddressPayload; skipValidation: boolean }>({
      query: ({ payload, skipValidation }) => ({
        url: `addresses/?skipAddressValidation=${skipValidation}`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: ['Addresses', 'Address']
    }),
    getWarehouseAddresses: builder.query<GetAddressesResponse, GetAddressesParams>({
      query: (params) => ({
        url: '/addresses',
        params: lodash.pickBy(params, (v) => v !== undefined)
      })
    }),
    getInfo: builder.query<{ version: string; apiName: string }, void>({
      async queryFn(args, queryApi, extraOptions, fetchWithBQ): Promise<any> {
        const version = await fetchWithBQ({ url: '/diagnostics/version', responseHandler: (response): Promise<string> => response.text() });
        const apiName = await fetchWithBQ({ url: '/diagnostics/apiName', responseHandler: (response): Promise<string> => response.text() });

        return { data: { version: version.data, apiName: apiName.data } };
      }
    })
  })
});

export const { useGetAddressesQuery, useLazyGetAddressesQuery, useGetAddressQuery, useCreateAddressMutation, useGetInfoQuery, useGetWarehouseAddressesQuery } = chuckieSueAddressesApi;
